import {
  SmaugClientConfig,
  HealthResponse,
  RatingRequest,
  RatingResponse,
  ErrorResponse,
  SmaugError,
  SmaugNetworkError
} from '../types';

const DEFAULT_TIMEOUT = 30000; // 30 seconds

export class SmaugClient {
  private baseUrl: string;
  private timeout: number;
  private headers: Record<string, string>;
  private authTokenProvider: (() => string | null) | undefined;

  constructor(config: SmaugClientConfig = {}) {
    const defaultBaseUrl = process.env['SMAUG_URL'];
    this.baseUrl = (config.baseUrl || defaultBaseUrl || '').replace(/\/$/, ''); // Remove trailing slash
    this.timeout = config.timeout || DEFAULT_TIMEOUT;
    this.headers = {
      'Content-Type': 'application/json',
      ...config.headers
    };
    this.authTokenProvider = config.authTokenProvider;
  }

  private getRequestHeaders(): Record<string, string> {
    const headers = { ...this.headers };

    if (this.authTokenProvider) {
      const authToken = this.authTokenProvider();
      if (authToken) {
        headers['Authorization'] = authToken;
      }
    }

    return headers;
  }

  private isNetworkError(error: Error): boolean {
    return (
      error.name === 'TypeError' ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network request failed') ||
      error.message.includes('ERR_NETWORK') ||
      error.message.includes('ERR_INTERNET_DISCONNECTED')
    );
  }

  private handleHttpError(response: Response, data?: any): SmaugError {
    const message = data?.error || data?.errors?.join(', ') || `HTTP ${response.status}: ${response.statusText}`;
    return new SmaugError(message, response.status, data);
  }

  private async request<T>(
    method: string,
    endpoint: string,
    body?: any,
    options?: RequestInit
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const requestOptions: RequestInit = {
      method,
      headers: this.getRequestHeaders(),
      signal: options?.signal || controller.signal,
      ...options
    };

    if (body && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      requestOptions.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, requestOptions);
      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorData: ErrorResponse | string | undefined;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            errorData = await response.json();
          } else {
            errorData = await response.text();
          }
        } catch {
          // Ignore parse errors for error responses
        }

        throw this.handleHttpError(response, errorData);
      }

      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        return await response.json();
      } else {
        return (await response.text()) as unknown as T;
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof SmaugError) {
        throw error;
      }

      if (this.isNetworkError(error as Error)) {
        throw new SmaugNetworkError(
          `Failed to connect to Smaug service at ${url}`,
          error as Error
        );
      }

      throw new SmaugError(
        `Request failed: ${(error as Error).message}`,
        undefined,
        undefined,
        error as Error
      );
    }
  }

  /**
   * Check the health status of the Smaug service
   */
  async healthCheck(): Promise<HealthResponse> {
    try {
      // Rails health endpoint is at /up
      const response = await this.request<HealthResponse>('GET', '/up');
      return response;
    } catch (error) {
      // If we get a response, even an error one, the service is reachable
      if (error instanceof SmaugError && error.statusCode) {
        return { status: 'reachable' };
      }
      throw error;
    }
  }

  /**
   * Submit a rating for a translation
   * @param rating - The rating data including correlation_id, rating ('up' for positive, 'down' for negative), and optional comments
   */
  async submitRating(rating: RatingRequest): Promise<RatingResponse> {
    if (!rating.correlation_id) {
      throw new SmaugError('correlation_id is required');
    }

    if (rating.rating !== 'up' && rating.rating !== 'down') {
      throw new SmaugError('rating must be either "up" or "down"');
    }

    return this.request<RatingResponse>('POST', '/api/v1/ratings', rating);
  }
}